import type { Schema } from '@/shared/api/contract';
import { formatBytes } from '@/shared/utils/format';

/**
 * The catalogs of the suppliers (contract v10.49, ADR 0044 of the back): the PDF goes from
 * the browser straight to the bucket with a signed link, the worker counts its pages and
 * looks for text, and the viewer reads it in pieces. Straight from the contract,
 * `cubas.domain.schemas.catalog` of the back.
 */
export type Catalog = Schema<'CatalogRead'>;
export type CatalogStatus = Schema<'CatalogStatus'>;
export type CatalogStarted = Schema<'CatalogUpload'>;
export type UploadLink = Schema<'UploadLinkRead'>;
export type CatalogLink = Schema<'CatalogLink'>;

/** `uploading` never reaches a list (the back leaves it out): it is the dialog's. */
export const CATALOG_STATUS_LABELS: Record<CatalogStatus, string> = {
  uploading: 'Subiendo',
  preparing: 'Preparándolo',
  ready: 'Listo',
  failed: 'No se pudo abrir',
};

/**
 * CAT-01…05 (`cubas.catalogs.library` of the back), as the line over the sentence the back
 * sends in `error`. CAT-03, CAT-04 and CAT-05 are not necessarily the file's: «Volver a
 * intentarlo» asks again (the back says so in `can_retry`).
 */
export function catalogFailureTitle(code: string | null | undefined): string {
  switch (code) {
    case 'CAT-01':
      return 'El PDF no se puede abrir';
    case 'CAT-02':
      return 'Lo que se subió no es un PDF';
    case 'CAT-04':
      return 'No se pudo pedir que se prepare';
    case 'CAT-05':
      return 'No se pudo leer o guardar el fichero en el almacén';
    default:
      return 'No se pudo preparar el catálogo';
  }
}

/** The name a catalog gets when nobody writes one: the file's, without `.pdf`. */
export function nameFromFile(filename: string): string {
  return filename.replace(/\.pdf$/i, '').trim() || filename;
}

export function isPdfFile(file: File): boolean {
  return file.type === 'application/pdf' || /\.pdf$/i.test(file.name);
}

export const CATALOG_ACCEPT = '.pdf,application/pdf';

export function catalogPath(catalog: Pick<Catalog, 'id'>): string {
  return `/documentos/catalogos/${catalog.id}`;
}

const COUNT = new Intl.NumberFormat('es-ES');

/** «464 páginas · 120 MB», and whether the text can be selected. */
export function catalogFacts(catalog: Catalog): string {
  const parts: string[] = [];
  if (catalog.pages) {
    parts.push(`${COUNT.format(catalog.pages)} ${catalog.pages === 1 ? 'página' : 'páginas'}`);
  }
  if (catalog.size) parts.push(formatBytes(catalog.size));
  if (catalog.has_text === false) parts.push('escaneado');
  return parts.join(' · ');
}

/** Shorter than this, a selection finds half the tariff. */
export const MIN_SELECTION = 2;
/** A reference is a few characters; a whole paragraph selected is cut. */
const MAX_CHARS = 120;

/** What was selected, as a search: one line, without the spaces pdf.js puts between pieces. */
export function selectionQuery(text: string): string {
  return text.replace(/\s+/g, ' ').trim().slice(0, MAX_CHARS).trim();
}
